/* eslint-disable react/jsx-props-no-spreading */
// vendors
import {
  Accordion as ReachAccordion,
  AccordionButton as ReachAccordionButton,
  AccordionItem as ReachAccordionItem,
  AccordionPanel as ReachAccordionPanel,
  useAccordionContext,
  useAccordionItemContext,
} from '@reach/accordion';
import '@reach/accordion/styles.css';
import classNames from 'classnames';
import { m as motion, useAnimation, useAnimationFrame } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

export function Accordion({ children, className, ...rest }) {
  return (
    <ReachAccordion
      className={classNames([className, 'border-t border-paleOrange-600'])}
      {...rest}
    >
      {children}
    </ReachAccordion>
  );
}

Accordion.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

Accordion.defaultProps = {
  className: null,
};

function InnerAccordionItem({ children }) {
  const ref = useRef(null);
  const { isExpanded, index } = useAccordionItemContext();
  const { openPanels } = useAccordionContext();
  const controls = useAnimation();
  const isAnimating = useRef(false);

  React.useLayoutEffect(() => {
    controls.start(isExpanded ? 'open' : 'collapsed');
  }, [isExpanded]);

  const handleComplete = () => {
    isAnimating.current = false;
  };

  const handleAnimationStart = () => {
    isAnimating.current = isExpanded;
  };

  useAnimationFrame(() => {
    if (isExpanded && isAnimating.current && openPanels.includes(index)) {
      const { offsetTop } = ref.current;

      if (offsetTop < window.scrollY) {
        window.scrollTo({ top: offsetTop, behavior: 'auto' });
      }
    }
  });

  return (
    <motion.div
      layout
      ref={ref}
      initial="collapsed"
      animate={controls}
      exit="collapsed"
      onAnimationStart={handleAnimationStart}
      onAnimationComplete={handleComplete}
    >
      {children}
    </motion.div>
  );
}

InnerAccordionItem.propTypes = {
  children: PropTypes.node.isRequired,
};

export function AccordionItem({ children, className, ...rest }) {
  return (
    <ReachAccordionItem
      className={classNames([
        className,
        'bg-white container border-b border-paleOrange-600',
      ])}
      {...rest}
    >
      <InnerAccordionItem>{children}</InnerAccordionItem>
    </ReachAccordionItem>
  );
}

AccordionItem.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

AccordionItem.defaultProps = {
  className: null,
};

export function AccordionButton({ children, className, ...rest }) {
  const { isExpanded } = useAccordionItemContext();
  return (
    <ReachAccordionButton
      className={classNames([
        className,
        'fluid-py-[1.25/2.5] flex justify-between items-center w-full aria-expanded:text-paleOrange-600 group transition-colors focus:outline-none',
      ])}
      {...rest}
    >
      {children}

      <span className="block">{isExpanded ? '-' : '+'}</span>
    </ReachAccordionButton>
  );
}

AccordionButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

AccordionButton.defaultProps = {
  className: null,
};

function ForwardedAccordionPanel({ children, className, ...rest }, ref) {
  return (
    <ReachAccordionPanel
      ref={ref}
      className={classNames([className])}
      {...rest}
    >
      <div className={classNames(['fluid-py-[1.25/2.5]'])}>{children}</div>
    </ReachAccordionPanel>
  );
}

ForwardedAccordionPanel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

ForwardedAccordionPanel.defaultProps = {
  className: null,
};

const MotionizedAccordionPanel = motion(
  React.forwardRef(ForwardedAccordionPanel),
  { forwardMotionProps: true }
);

export function AccordionPanel({ children, className, ...rest }) {
  return (
    <MotionizedAccordionPanel
      className={classNames(['overflow-hidden', className])}
      layout
      variants={{
        open: { opacity: 1, height: 'auto', pointerEvents: 'auto' },
        collapsed: { opacity: 0, height: 0, pointerEvents: 'none' },
      }}
      {...rest}
    >
      {children}
    </MotionizedAccordionPanel>
  );
}

AccordionPanel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

AccordionPanel.defaultProps = {
  className: null,
};
